import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/site";

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-md border-b border-[#dddddd]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                <div className="h-20 md:h-24 flex items-center justify-between">
                    {/* Wordmark */}
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="font-serif text-2xl md:text-[1.7rem] tracking-tight text-[#111111] leading-none"
                    >
                        Pixemance
                        <span className="text-[#444444]">.</span>
                    </Link>

                    {/* Center nav */}
                    <nav className="hidden md:flex items-center gap-10">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-[13px] tracking-wide font-medium link-underline ${
                                        isActive
                                            ? "text-[#111111]"
                                            : "text-[#444444] hover:text-[#111111]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Right CTA */}
                    <div className="hidden md:block">
                        <Link
                            to="/contact"
                            data-testid="nav-cta"
                            className="group inline-flex items-center gap-3 bg-[#111111] text-white px-6 py-3 text-[13px] tracking-wide font-medium hover:bg-[#444444] transition-colors duration-300"
                        >
                            Book Strategy Call
                            <span
                                aria-hidden
                                className="inline-block transition-transform duration-500 group-hover:translate-x-1"
                            >
                                →
                            </span>
                        </Link>
                    </div>

                    {/* Mobile toggle */}
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-[#111111]"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden border-t border-[#dddddd] bg-white"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className="font-serif text-3xl text-[#111111]"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta"
                            className="mt-4 inline-flex items-center justify-between bg-[#111111] text-white px-6 py-4 text-sm tracking-wide"
                        >
                            Book Strategy Call <span aria-hidden>→</span>
                        </Link>
                        <p className="text-xs tracking-[0.25em] uppercase text-[#444444] mt-2">
                            {BRAND.positioning}
                        </p>
                    </div>
                </div>
            )}
        </header>
    );
};
